// client/src/coms/interactions/interactionStore.js
// Interaction cards on screen share one store: cards ask for their message id, the store batches the requests
// (GET /interactions/by-messages), and refreshes a card when the channel stream says it changed
// ('vox-interaction-update', dispatched by useChatMessages for 'interaction_update' events) or when the stream
// had to resync. The server shapes every interaction for the member, so nothing is computed from events.
//
// Countdowns and remaining times render locally from server timestamps: the store keeps the offset between the
// server clock (server_now_ms in every response) and this device, and one shared 1-second ticker re-renders the
// components that show a running time. No per-viewer timers run on the server.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { comsApiDelete, comsApiGet, comsApiPatch, comsApiPost } from '../comsApi';

const byMessage = new Map(); // message id -> interaction
const byId = new Map(); // interaction id -> message id
const missing = new Set(); // message ids the server did not return (deleted or not visible)
const listeners = new Map(); // message id -> Set<fn>
let pending = new Set();
let flushTimer = null;
let clockOffset = 0; // server - device

function emit(messageId) {
  const set = listeners.get(String(messageId));
  if (set) for (const fn of Array.from(set)) fn();
}

function remember(it) {
  if (!it || !it.message_id) return;
  if (it.server_now_ms) clockOffset = Number(it.server_now_ms) - Date.now();
  const key = String(it.message_id);
  const prev = byMessage.get(key);
  // Detail responses carry everything the card needs too; never replace newer data with older.
  if (prev && prev.version > it.version) return;
  byMessage.set(key, it);
  byId.set(String(it.id), key);
  missing.delete(key);
  emit(key);
}

async function flush() {
  flushTimer = null;
  const ids = Array.from(pending).slice(0, 50);
  pending = new Set(Array.from(pending).slice(50));
  if (pending.size) flushTimer = setTimeout(flush, 0);
  if (!ids.length) return;
  try {
    const r = await comsApiGet(`/interactions/by-messages?ids=${ids.join(',')}`);
    if (r && r.server_now_ms) clockOffset = Number(r.server_now_ms) - Date.now();
    const got = new Set();
    for (const it of (r && r.interactions) || []) {
      remember(it);
      got.add(String(it.message_id));
    }
    for (const id of ids) {
      if (!got.has(id)) {
        missing.add(id);
        emit(id);
      }
    }
  } catch {
    for (const id of ids) emit(id);
  }
}

function request(messageId) {
  const key = String(messageId || '');
  if (!key) return;
  pending.add(key);
  if (!flushTimer) flushTimer = setTimeout(flush, 30);
}

export function serverNow() {
  return Date.now() + clockOffset;
}

export function getInteraction(messageId) {
  return byMessage.get(String(messageId)) || null;
}

export function refreshByInteractionId(interactionId, messageId) {
  const key = messageId ? String(messageId) : byId.get(String(interactionId));
  if (key && listeners.has(key)) request(key);
}

// Applies a server response to an action (vote, RSVP ...) without waiting for the stream event.
export function applyResponse(r) {
  if (r && r.interaction) remember(r.interaction);
  return r;
}

if (typeof window !== 'undefined') {
  window.addEventListener('vox-interaction-update', (e) => {
    const d = (e && e.detail) || {};
    const key = d.message_id ? String(d.message_id) : byId.get(String(d.interaction_id));
    const cur = key ? byMessage.get(key) : null;
    if (key && listeners.has(key) && (!cur || !d.version || Number(d.version) > Number(cur.version))) request(key);
  });
  // The stream missed events (server restart, long disconnect), the device was offline, or the tab was in the
  // background (a stream can stay 'open' through a network drop without delivering anything): refresh every
  // card on screen once.
  let resyncTimer = null;
  const resyncAll = () => {
    if (resyncTimer) return;
    resyncTimer = setTimeout(() => {
      resyncTimer = null;
      for (const key of listeners.keys()) request(key);
    }, 400);
  };
  window.addEventListener('vox-interaction-resync', resyncAll);
  window.addEventListener('online', resyncAll);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resyncAll();
  });
}

export function useInteraction(messageId) {
  const key = String(messageId || '');
  const [, force] = useState(0);
  useEffect(() => {
    if (!key) return undefined;
    let set = listeners.get(key);
    if (!set) {
      set = new Set();
      listeners.set(key, set);
    }
    const fn = () => force((n) => n + 1);
    set.add(fn);
    if (!byMessage.has(key)) request(key);
    return () => {
      set.delete(fn);
      if (!set.size) listeners.delete(key);
    };
  }, [key]);
  return { interaction: byMessage.get(key) || null, missing: missing.has(key) };
}

// ---- shared ticker ----
const tickSubs = new Set();
let tickTimer = null;
let tickNow = Date.now();
function subscribeTick(fn) {
  tickSubs.add(fn);
  if (!tickTimer) {
    tickTimer = setInterval(() => {
      tickNow = Date.now();
      for (const f of Array.from(tickSubs)) f();
    }, 1000);
  }
  return () => {
    tickSubs.delete(fn);
    if (!tickSubs.size && tickTimer) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  };
}

// Server-synchronised "now" that updates once a second while the component is mounted (active = false stops it).
export function useServerClock(active = true) {
  const subscribe = active ? subscribeTick : () => () => {};
  const t = useSyncExternalStore(subscribe, () => (active ? tickNow : 0), () => 0);
  return (t || Date.now()) + clockOffset;
}

// ---- API helpers ----
export const interactionApi = {
  get: (id) => comsApiGet(`/interactions/${id}`).then(applyResponse),
  vote: (id, body) => comsApiPost(`/interactions/${id}/vote`, body).then(applyResponse),
  unvote: (id) => comsApiDelete(`/interactions/${id}/vote`).then(applyResponse),
  close: (id) => comsApiPost(`/interactions/${id}/close`, {}).then(applyResponse),
  reopen: (id) => comsApiPost(`/interactions/${id}/reopen`, {}).then(applyResponse),
  remove: (id) => comsApiDelete(`/interactions/${id}`),
  edit: (id, body) => comsApiPatch(`/interactions/${id}`, body).then(applyResponse),
  confirm: (id, optionId) => comsApiPost(`/interactions/${id}/confirm`, { option_id: optionId }).then(applyResponse),
  rsvp: (id, status) => comsApiPost(`/interactions/${id}/rsvp`, { status }).then(applyResponse),
  subscribe: (id, on) => comsApiPost(`/interactions/${id}/subscribe`, { on }).then(applyResponse),
  status: (id, status, note) => comsApiPost(`/interactions/${id}/status`, { status, note }).then(applyResponse),
  addItem: (id, body) => comsApiPost(`/interactions/${id}/items`, body).then(applyResponse),
  editItem: (id, itemId, body) => comsApiPatch(`/interactions/${id}/items/${itemId}`, body).then(applyResponse),
  removeItem: (id, itemId) => comsApiDelete(`/interactions/${id}/items/${itemId}`).then(applyResponse),
  reorder: (id, ids) => comsApiPost(`/interactions/${id}/items/reorder`, { ids }).then(applyResponse),
  upvoteItem: (id, itemId, on) => comsApiPost(`/interactions/${id}/items/${itemId}/upvote`, { on }).then(applyResponse),
  assignable: (id) => comsApiGet(`/interactions/${id}/assignable`),
  capabilities: (kind, channelId) => comsApiGet(`/interactions/capabilities?kind=${kind === 'voice' ? 'voice' : 'text'}&channel_id=${Number(channelId) || 0}`),
  create: (kind, channelId, body) => comsApiPost(`/${kind === 'voice' ? 'voice' : 'text'}-channels/${Number(channelId) || 0}/interactions`, body).then(applyResponse),
  list: (kind, channelId, params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return comsApiGet(`/${kind === 'voice' ? 'voice' : 'text'}-channels/${Number(channelId) || 0}/interactions${qs ? `?${qs}` : ''}`);
  },
  templates: (serverKey) => comsApiGet(`/servers/${encodeURIComponent(serverKey)}/interaction-templates`),
  saveTemplate: (serverKey, body) => comsApiPost(`/servers/${encodeURIComponent(serverKey)}/interaction-templates`, body),
  deleteTemplate: (id) => comsApiDelete(`/interaction-templates/${id}`),
};

// Error text from an API failure (the server sends { error }).
export function errorText(e, fallback = 'Something went wrong') {
  return (e && e.data && e.data.error) || (e && e.status === 429 ? 'Slow down a little and try again.' : '') || fallback;
}

// Opens the creation dialog: window event handled by InteractionHost (mounted once in ComsLayout).
export function openInteractionCreator(detail) {
  try {
    window.dispatchEvent(new CustomEvent('vox-interaction-create', { detail }));
  } catch {}
}

export function openInteractionBrowser(detail) {
  try {
    window.dispatchEvent(new CustomEvent('vox-interaction-browse', { detail }));
  } catch {}
}

export const TYPE_META = {
  poll: { label: 'Poll', icon: '📊', hint: 'Ask a question with choices' },
  event: { label: 'Event', icon: '📅', hint: 'Find a time and collect RSVPs' },
  decision: { label: 'Decision', icon: '⚖️', hint: 'Formal Yes / No / Abstain vote' },
  tasks: { label: 'Task list', icon: '✅', hint: 'Shared checklist with assignees' },
  qa: { label: 'Q&A session', icon: '❓', hint: 'Collect and upvote questions' },
  suggestion: { label: 'Suggestion', icon: '💡', hint: 'Propose an idea for upvotes' },
  countdown: { label: 'Countdown', icon: '⏳', hint: 'Count down to a moment' },
};
export const TYPE_ORDER = ['poll', 'event', 'decision', 'tasks', 'qa', 'suggestion', 'countdown'];

// Creation capabilities per channel ('text:12' -> { create: {...} }), cached briefly; refreshed on permission changes.
const capsCache = new Map();
export function useInteractionCapabilities(kind, channelId) {
  const key = kind && channelId ? `${kind}:${channelId}` : '';
  const [caps, setCaps] = useState(() => (key && capsCache.get(key) ? capsCache.get(key).value : null));
  useEffect(() => {
    if (!key) {
      setCaps(null);
      return undefined;
    }
    let alive = true;
    const load = (force) => {
      const hit = capsCache.get(key);
      if (!force && hit && Date.now() - hit.at < 15000) {
        setCaps(hit.value);
        return;
      }
      interactionApi
        .capabilities(kind, channelId)
        .then((r) => {
          capsCache.set(key, { at: Date.now(), value: r });
          if (alive) setCaps(r);
        })
        .catch(() => alive && setCaps({ create: {} }));
    };
    load(false);
    const onChange = () => load(true);
    window.addEventListener('vox-permissions-changed', onChange);
    return () => {
      alive = false;
      window.removeEventListener('vox-permissions-changed', onChange);
    };
  }, [key, kind, channelId]);
  return caps;
}
